<script setup>
import { useI18n } from 'vue-i18n';
import { startMessages } from './startSchedule';
import en from './en.json';
import ru from './ru.json';

defineProps({ catalog: { type: Object, required: true } });
const model = defineModel({ type: Object, required: true });
const { t } = useI18n({
  useScope: 'local',
  messages: {
    en: { ...en, ...startMessages.en },
    ru: { ...ru, ...startMessages.ru },
  },
});
const changeMode = () => {
  if (model.value.mode === 'event') model.value.entity = 'appointments';
};
</script>

<template>
  <div class="space-y-4">
    <fieldset>
      <legend class="mb-2 text-xs text-n-slate-11">
        {{ t('startSchedule.mode') }}
      </legend>
      <div class="flex gap-4">
        <label
          v-for="mode in ['event', 'schedule']"
          :key="mode"
          class="flex items-center gap-2 text-sm cursor-pointer"
        >
          <input
            v-model="model.mode"
            type="radio"
            name="scenario-start-mode"
            :value="mode"
            @change="changeMode"
          />
          {{ t(`startSchedule.${mode}`) }}
        </label>
      </div>
    </fieldset>
    <template v-if="model.mode === 'event'">
      <label class="block text-xs"
        >{{ t('event') }}
        <select v-model="model.event" class="w-full mt-1 text-sm">
          <option v-if="!model.event" :value="model.event" disabled>
            {{ t('eventNotSelected') }}
          </option>
          <option v-for="event in catalog.events" :key="event" :value="event">
            {{ t(event) }}
          </option>
        </select>
      </label>
      <label v-if="model.event === 'response'" class="block text-xs">
        {{ t('runtime.responseAction') }}
        <select v-model="model.response_action" class="w-full mt-1 text-sm">
          <option
            v-for="action in catalog.response_actions"
            :key="action"
            :value="action"
          >
            {{ t(`runtime.actions.${action.replace('.', '_')}`) }}
          </option>
        </select>
      </label>
      <p class="m-0 text-xs leading-5 text-n-slate-11">
        {{ t('triggerHint') }}
      </p>
    </template>
    <template v-else>
      <label class="block text-xs"
        >{{ t('startSchedule.entity') }}
        <select v-model="model.entity" class="w-full mt-1 text-sm">
          <option
            v-for="entity in ['patients', 'appointments']"
            :key="entity"
            :value="entity"
          >
            {{ t(`startSchedule.${entity}`) }}
          </option>
        </select>
      </label>
      <label class="block text-xs"
        >{{ t('startSchedule.frequency') }}
        <select v-model="model.schedule.frequency" class="w-full mt-1 text-sm">
          <option
            v-for="frequency in ['once', 'daily', 'weekly']"
            :key="frequency"
            :value="frequency"
          >
            {{ t(`startSchedule.${frequency}`) }}
          </option>
        </select>
      </label>
      <fieldset v-if="model.schedule.frequency === 'weekly'">
        <legend class="mb-2 text-xs">{{ t('startSchedule.weekdays') }}</legend>
        <div class="flex flex-wrap gap-x-4 gap-y-2">
          <label
            v-for="day in 7"
            :key="day"
            class="flex items-center gap-1.5 text-xs cursor-pointer"
          >
            <input
              v-model="model.schedule.weekdays"
              type="checkbox"
              :value="day"
            />{{ t(`startSchedule.day${day}`) }}
          </label>
        </div>
      </fieldset>
      <div class="grid grid-cols-2 gap-3">
        <label v-if="model.schedule.frequency === 'once'" class="text-xs">
          {{ t('startSchedule.date') }}
          <input
            v-model="model.schedule.date"
            type="date"
            class="w-full mt-1 text-sm"
          />
        </label>
        <label class="text-xs"
          >{{ t('startSchedule.time') }}
          <input
            v-model="model.schedule.time"
            type="time"
            class="w-full mt-1 text-sm"
          />
        </label>
        <label
          class="text-xs"
          :class="{ 'col-span-2': model.schedule.frequency === 'once' }"
          >{{ t('startSchedule.timezone') }}
          <select v-model="model.schedule.timezone" class="w-full mt-1 text-sm">
            <option v-for="zone in catalog.timezones" :key="zone" :value="zone">
              {{ zone }}
            </option>
          </select>
        </label>
      </div>
      <div class="space-y-2 text-xs leading-5 text-n-slate-11">
        <p class="m-0">
          {{
            t(
              model.entity === 'patients'
                ? 'startSchedule.patientHint'
                : 'startSchedule.appointmentHint'
            )
          }}
        </p>
        <details class="space-y-2">
          <summary class="cursor-pointer text-n-brand">
            {{ t('startSchedule.rules') }}
          </summary>
          <p class="m-0">{{ t('startSchedule.hint') }}</p>
          <p class="m-0">{{ t('startSchedule.timezoneHint') }}</p>
          <p class="m-0">{{ t('startSchedule.testHint') }}</p>
        </details>
      </div>
    </template>
  </div>
</template>
